import React, { useState, useContext, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  message,
  Row,
  Col,
  InputNumber,
  Tooltip,
  Checkbox,
} from "antd";
import {
  UserOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  BellOutlined,
} from "@ant-design/icons";
import HandymanModel from "../lib/firebase/handymanModel";
import { trackEvent } from "../helpers/mixpanel";
import { formatEmailToLowercase } from "../utils/emailUtils";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../context/AuthProvider";
import styled from "styled-components";
import { useBreakpoints } from "../lib/breakpoints";

const { Option } = Select;

const StyledModal = styled(Modal)`
  .ant-select-arrow {
    margin-top: -6px !important;
  }

  .ant-modal-header {
    padding: 1.25rem 1.5rem;
    border-bottom: none;
    background: var(--color-primary);
    border-radius: 12px 12px 0 0;
    margin-bottom: 0 !important;
  }
  .ant-modal-title {
    color: #fff;
    font-weight: 600;
  }
  .ant-modal-body {
    padding: 0;
    background: #fff;
  }
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    padding: 0 !important;
  }

  .ant-modal-close {
    color: white !important;
    .anticon,
    .anticon svg,
    svg {
      color: white !important;
      fill: white !important;
    }
    &:hover,
    &:hover .anticon,
    &:hover .anticon svg,
    &:hover svg {
      color: #fff !important;
      fill: #fff !important;
    }
  }

  @media (max-width: 768px) {
    .ant-modal-body {
      padding: 0;
    }
    .ant-modal-content {
      margin: 0 0.5rem;
    }
  }
`;

const SectionTitle = styled.h4`
  color: var(--color-text);
  font-size: 1rem;
  font-weight: 600;
  margin-top: 1rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--color-border-light);
  display: flex;
  align-items: center;
`;

const InfoIcon = styled(InfoCircleOutlined)`
  color: var(--color-text-muted);
  margin-left: 0.5rem;
`;

const StyledForm = styled(Form)`
  .ant-form-item-label > label,
  .ant-form-item-label,
  .ant-form-item-label > label.ant-form-item-required,
  .ant-form-item-label > label.ant-form-item-required::before,
  label {
    color: var(--color-text) !important;
    font-weight: 600 !important;
    font-size: 0.875rem !important;
    font-family: var(--font-primary) !important;
    line-height: 1.4 !important;
    // margin-bottom: 0.5rem !important;
  }

  .ant-form-item-label > label.ant-form-item-required::before {
    font-size: 0.875rem !important;
    font-weight: 600 !important;
    color: #ff4d4f !important;
  }

  .ant-form-item-label > label span {
    font-size: 0.875rem !important;
    font-weight: 600 !important;
    font-family: var(--font-primary) !important;
  }

  /* Refined spacing system */
  .ant-form-item {
    margin-top: 0 !important;
    margin-bottom: 0.75rem !important;
  }

  /* Tighter spacing for form rows */
  .ant-row {
    margin-top: 0 !important;
    margin-bottom: 0.5rem !important;
  }

  /* Consistent label spacing */
  .ant-form-item-label {
    margin-bottom: 0rem !important;
  }

  /* Reduce spacing between form items in same row */
  .ant-col + .ant-col .ant-form-item {
    margin-top: 0 !important;
  }

  .ant-input,
  .ant-input-number,
  .ant-select-selector,
  .ant-picker {
    border: 2px solid var(--color-border-light);
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    height: 44px !important;
    line-height: 1.5;
    display: flex;
    align-items: center;
    font-size: 0.875rem !important;
    font-family: var(--font-primary) !important;
  }

  /* Style affix-wrapper (used by InputNumber with prefix) */
  .ant-input-affix-wrapper {
    border: 2px solid var(--color-border-light) !important;
    border-radius: 8px !important;
    padding: 0.5rem 0.75rem !important;
    height: 44px !important;

    /* Remove inner separator border */
    .ant-input-prefix {
      border-right: none !important;
      border: none !important;
      margin-right: 8px !important;
      padding-right: 0 !important;
    }

    /* Ensure InputNumber inside has no border */
    .ant-input-number {
      border: none !important;

      .ant-input-number-input {
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
      }
    }
  }

  .ant-select {
    min-height: 44px !important;
    font-size: 0.875rem !important;

    .ant-select-selector {
      min-height: 44px !important;
      height: auto !important;
      padding: 0.5rem 0.75rem !important;
      display: flex !important;
      align-items: flex-start !important;
      flex-wrap: wrap !important;
      overflow: visible !important;
    }

    .ant-select-selection-overflow {
      display: flex !important;
      flex-wrap: wrap !important;
      align-items: flex-start !important;
      overflow: visible !important;
    }

    .ant-select-selection-placeholder {
      line-height: 1.5 !important;
      font-size: 0.875rem !important;
    }

    /* Specialty tags styling */
    .ant-select-selection-item {
      font-size: 0.875rem !important;
      line-height: 1.5 !important;
      height: 28px !important;
      padding: 0 8px !important;
      display: inline-flex !important;
      align-items: center !important;
      background: #f5f5f5 !important;
      border: 1px solid #d9d9d9 !important;
      border-radius: 6px !important;
      margin-right: 4px !important;
      margin-bottom: 4px !important;
      margin-top: 2px !important;
      flex-shrink: 0 !important;
      white-space: nowrap !important;
      max-width: none !important;
      border: none !important;
    }

    .ant-select-selection-item-content {
      font-size: 0.875rem !important;
      line-height: 1.5 !important;
    }

    .ant-select-selection-item-remove {
      font-size: 0.75rem !important;
      margin-left: 4px !important;
    }

    /* Search input styling */
    .ant-select-selection-search {
      margin-top: 2px !important;
      margin-bottom: 2px !important;
      flex-shrink: 0 !important;
      min-width: 20px !important;
    }

    .ant-select-selection-search-input {
      font-size: 0.875rem !important;
      line-height: 1.5 !important;
    }

    /* Ensure proper wrapping behavior */
    .ant-select-selection-overflow-item {
      flex-shrink: 0 !important;
    }
  }

  .ant-input:focus,
  .ant-select-focused .ant-select-selector,
  .ant-picker-focused,
  .ant-input-number:focus {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.1) !important;
  }

  .ant-form-item-required::before {
    color: #ff4d4f !important;
    font-size: 0.875rem !important;
  }

  .ant-input[disabled] {
    background-color: #f5f5f5 !important;
    color: #999 !important;
  }

  .ant-select-disabled .ant-select-selector {
    background-color: #f5f5f5 !important;
    color: #999 !important;
  }

  /* Response time dropdown styling */
  .response-time-select {
    background: transparent !important;
  }
  .response-time-select.ant-select,
  .response-time-select.ant-select-outlined {
    background: transparent !important;
  }
  .response-time-select.ant-select .ant-select-selector,
  .response-time-select.ant-select-outlined .ant-select-selector,
  .response-time-select.ant-select-outlined:not(.ant-select-customize-input)
    .ant-select-selector,
  .response-time-select .ant-select-selector {
    background: #f7f8fa !important;
    background-color: #f7f8fa !important;
    border: none !important;
    border-color: transparent !important;
    border-radius: 12px !important;
    height: 44px !important;
    padding: 0.5rem 0.75rem !important;
    display: flex !important;
    align-items: center !important;
    box-shadow: none !important;
  }
  .response-time-select .ant-select-selector:hover,
  .response-time-select.ant-select-focused .ant-select-selector,
  .response-time-select.ant-select-open .ant-select-selector,
  .response-time-select.ant-select-outlined .ant-select-selector:hover,
  .response-time-select.ant-select-outlined.ant-select-focused
    .ant-select-selector,
  .response-time-select.ant-select-outlined.ant-select-open
    .ant-select-selector,
  .response-time-select.ant-select-outlined:not(
      .ant-select-customize-input
    ).ant-select-focused
    .ant-select-selector,
  .response-time-select.ant-select-outlined:not(
      .ant-select-customize-input
    ).ant-select-open
    .ant-select-selector {
    background: #f7f8fa !important;
    background-color: #f7f8fa !important;
    border: none !important;
    border-color: transparent !important;
    box-shadow: none !important;
  }
  .response-time-select .ant-select-selector::after,
  .response-time-select .ant-select-selector::before {
    display: none !important;
  }
  .response-time-select .ant-select-selection-item {
    background: #f7f8fa !important;
    background-color: #f7f8fa !important;
    display: flex !important;
    align-items: center !important;
  }
  .response-time-select .ant-select-arrow {
    background: transparent !important;
  }

  .response-time-select .ant-select-selection-item,
  .response-time-select .ant-select-selection-placeholder {
    display: flex !important;
    align-items: center !important;
    align-self: center !important;
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1.2 !important;
  }

  .response-time-select .ant-select-selection-wrap {
    display: flex !important;
    align-items: center !important;
    height: 100% !important;
  }

  /* Specialties dropdown: match input height on single line, expand when needed */
  .specialties-select .ant-select-selector {
    min-height: 44px !important;
    height: auto !important;
    display: flex !important;
    align-items: flex-start !important;
    flex-wrap: wrap !important;
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
    overflow: visible !important;
  }
  .specialties-select .ant-select-selection-item {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    height: 28px !important;
  }
  .specialties-select .ant-select-selection-overflow {
    flex-wrap: wrap !important;
    gap: 4px !important;
  }
  .specialties-select .ant-select-selection-search {
    margin-top: 2px !important;
    margin-bottom: 2px !important;
  }
`;

const HandymanModal = ({ visible, onClose, handyman = null, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { isMobile } = useBreakpoints();
  const isEditing = !!handyman;

  // Set form values when editing
  useEffect(() => {
    if (isEditing && handyman) {
      form.setFieldsValue({
        name: handyman.name,
        email: handyman.email,
        phone: handyman.phone,
        businessName: handyman.businessName,
        contactPerson: handyman.contactPerson,
        specialties: handyman.specialties,
        hourlyRate: handyman.hourlyRate,
        flatRate: handyman.flatRate,
        responseTime: handyman.responseTime,
        notes: handyman.notes,
        receiveAllTickets: handyman.receiveAllTickets || false,
        joinAllGroupChats: handyman.joinAllGroupChats || false,
      });
    } else {
      form.resetFields();
    }
  }, [isEditing, handyman, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Filter out undefined values - Firestore doesn't allow undefined
      const cleanedValues = Object.fromEntries(
        Object.entries(values).filter(([_, value]) => value !== undefined)
      );

      const handymanData = {
        ...cleanedValues,
        email: formatEmailToLowercase(values.email),
        createdAt: isEditing ? handyman.createdAt : new Date(),
        updatedAt: new Date(),
      };

      if (isEditing) {
        await HandymanModel.update(handyman.id, handymanData);
        message.success("Service team member updated successfully");
        trackEvent("handyman_updated", { handymanId: handyman.id });
      } else {
        await HandymanModel.create(handymanData);
        message.success("Service team member created successfully");
        trackEvent("handyman_created");
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["handymen", user?.uid] });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error saving handyman:", error);
      message.error(
        isEditing
          ? "Failed to update service team member"
          : "Failed to create service team member"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const specialtyOptions = [
    { value: "plumbing", label: "Plumbing" },
    { value: "electrical", label: "Electrical" },
    { value: "hvac", label: "HVAC" },
    { value: "appliances", label: "Appliance repair" },
    { value: "structural", label: "Structural" },
    { value: "cosmetic", label: "Cosmetic" },
    { value: "cleaning", label: "Cleaning" },
    { value: "landscaping", label: "Landscaping" },
    { value: "painting", label: "Painting" },
    { value: "flooring", label: "Flooring" },
    { value: "roofing", label: "Roofing" },
    { value: "other", label: "Other" },
  ];

  const responseTimeOptions = [
    { value: "same-day", label: "Same Day" },
    { value: "24-hours", label: "24 Hours" },
    { value: "48-hours", label: "48 Hours" },
    { value: "1-week", label: "1 Week" },
    { value: "flexible", label: "Flexible" },
  ];

  return (
    <StyledModal
      open={visible}
      onCancel={handleCancel}
      footer={null}
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            color: "white",
          }}
        >
          <UserOutlined
            style={{ fontSize: "1.5rem", filter: "brightness(0) invert(1)" }}
          />
          {isEditing
            ? "Edit Service Team Member"
            : "Add New Service Team Member"}
        </div>
      }
      destroyOnClose
      width={820}
    >
      <StyledForm
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ padding: "1.5rem" }}
      >
        <SectionTitle>
          <UserOutlined
            style={{ marginRight: "8px", color: "var(--color-primary)" }}
          />{" "}
          Contact Information
        </SectionTitle>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Please enter a name" }]}
            >
              <Input placeholder="Full name" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please enter an email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="email@example.com" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="phone"
              label="Phone"
              rules={[
                { required: true, message: "Please enter a phone number" },
              ]}
            >
              <Input
                placeholder="(555) 123-4567"
                maxLength={14}
                onChange={(e) => {
                  const input = e.target.value;
                  const numbers = input.replace(/\D/g, "");

                  // Allow up to 10 digits
                  if (numbers.length <= 10) {
                    let formatted = "";

                    if (numbers.length === 0) {
                      formatted = "";
                    } else if (numbers.length <= 3) {
                      formatted = `(${numbers}`;
                    } else if (numbers.length <= 6) {
                      formatted = `(${numbers.slice(0, 3)}) ${numbers.slice(
                        3
                      )}`;
                    } else {
                      formatted = `(${numbers.slice(0, 3)}) ${numbers.slice(
                        3,
                        6
                      )}-${numbers.slice(6)}`;
                    }

                    form.setFieldValue("phone", formatted);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="businessName"
              label="Business/Individual Name"
              rules={[
                {
                  required: true,
                  message: "Please enter a business or individual name",
                },
              ]}
            >
              <Input placeholder="Business or individual name" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="contactPerson" label="Contact Person">
              <Input placeholder="Primary contact person" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="specialties"
              label="Specialties"
              rules={[{ required: true, message: "Please select specialties" }]}
            >
              <Select
                className="specialties-select"
                mode="multiple"
                placeholder="Select specialties"
                options={specialtyOptions}
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <SectionTitle>
          <FileTextOutlined
            style={{ marginRight: "8px", color: "var(--color-primary)" }}
          />{" "}
          Service Details
        </SectionTitle>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="hourlyRate"
              label={
                <span>
                  Hourly Rate
                  <Tooltip title="Rate per hour">
                    <InfoIcon />
                  </Tooltip>
                </span>
              }
            >
              <InputNumber
                placeholder="0.00"
                prefix="$"
                min={0}
                step={0.01}
                style={{ width: "100%" }}
                formatter={(value) =>
                  typeof value === "number" ||
                  (typeof value === "string" && value !== "")
                    ? `${String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
                    : ""
                }
                parser={(value) => (value || "").replace(/(,*)/g, "")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="flatRate"
              label={
                <span>
                  Flat Rate
                  <Tooltip title="Fixed rate for specific services">
                    <InfoIcon />
                  </Tooltip>
                </span>
              }
            >
              <InputNumber
                placeholder="0.00"
                prefix="$"
                min={0}
                step={0.01}
                style={{ width: "100%" }}
                formatter={(value) =>
                  typeof value === "number" ||
                  (typeof value === "string" && value !== "")
                    ? `${String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
                    : ""
                }
                parser={(value) => (value || "").replace(/(,*)/g, "")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="responseTime"
              label={
                <span>
                  Response Time
                  <Tooltip title="Typical response time">
                    <InfoIcon />
                  </Tooltip>
                </span>
              }
            >
              <Select
                className="response-time-select"
                placeholder="Select response time"
                options={responseTimeOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <SectionTitle>
          <FileTextOutlined
            style={{ marginRight: "8px", color: "var(--color-primary)" }}
          />{" "}
          Additional Information
        </SectionTitle>
        <Form.Item name="notes" label="">
          <Input.TextArea
            rows={4}
            placeholder="Additional notes or comments about this service team member..."
            style={{
              minHeight: "80px",
              maxHeight: "120px",
              resize: "vertical",
            }}
          />
        </Form.Item>

        <SectionTitle style={{ marginTop: "1.5rem" }}>
          <BellOutlined
            style={{ marginRight: "8px", color: "var(--color-primary)" }}
          />{" "}
          Notification Settings
        </SectionTitle>
        <Form.Item name="receiveAllTickets" valuePropName="checked">
          <Checkbox>
            <span style={{ fontSize: "0.95rem" }}>
              Receive all maintenance tickets via email
              <span
                style={{
                  fontSize: "0.85em",
                  color: "var(--color-text-light)",
                  fontStyle: "italic",
                  marginLeft: "4px",
                }}
              >
                (SMS coming soon)
              </span>
            </span>
            <Tooltip title="When enabled, this service team member will receive an email notification for every maintenance ticket created, in addition to the landlord. SMS text notifications will be available soon. Multiple service team members can have this option enabled.">
              <InfoCircleOutlined
                style={{ marginLeft: "8px", color: "var(--color-text-light)" }}
              />
            </Tooltip>
          </Checkbox>
        </Form.Item>
        <Form.Item name="joinAllGroupChats" valuePropName="checked">
          <Checkbox>
            <span style={{ fontSize: "0.95rem" }}>
              Automatically add to tenant chats
            </span>
            <Tooltip title="When enabled, this service team member will automatically be added to tenant chat conversations when they are assigned to a maintenance ticket. This allows them to communicate directly with tenants about maintenance issues.">
              <InfoCircleOutlined
                style={{ marginLeft: "8px", color: "var(--color-text-light)" }}
              />
            </Tooltip>
          </Checkbox>
        </Form.Item>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: isEditing && !isMobile ? "space-between" : "center",
            gap: "0.75rem",
            alignItems: "stretch",
            marginTop: isMobile ? "1rem" : "2rem",
            paddingTop: !isMobile ? "1rem" : "0",
          }}
        >
          {isEditing && (
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                width: isMobile ? "100%" : "auto",
              }}
            >
              <Button
                danger
                onClick={() => {
                  Modal.confirm({
                    title: "Delete Service Team Member",
                    content:
                      "Are you sure you want to delete this service team member? This action cannot be undone.",
                    okText: "Delete",
                    okType: "danger",
                    cancelText: "Cancel",
                    onOk: async () => {
                      try {
                        setLoading(true);
                        await HandymanModel.delete(handyman.id);
                        message.success(
                          "Service team member deleted successfully"
                        );
                        trackEvent("handyman_deleted", {
                          handymanId: handyman.id,
                        });

                        // Invalidate queries to refresh data
                        queryClient.invalidateQueries({
                          queryKey: ["handymen", user?.uid],
                        });

                        onSuccess?.();
                        onClose();
                      } catch (error) {
                        console.error("Error deleting handyman:", error);
                        message.error("Failed to delete service team member");
                      } finally {
                        setLoading(false);
                      }
                    },
                  });
                }}
                style={isMobile ? { flex: 1 } : {}}
              >
                Delete
              </Button>
              <Button
                onClick={handleCancel}
                style={isMobile ? { flex: 1 } : {}}
              >
                Cancel
              </Button>
            </div>
          )}
          {!isEditing && (
            <Button
              onClick={handleCancel}
              style={isMobile ? { width: "100%" } : {}}
            >
              Cancel
            </Button>
          )}
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{
              width: isMobile ? "100%" : isEditing ? "auto" : "auto",
              marginLeft: !isMobile && isEditing ? "auto" : "0",
            }}
          >
            {isEditing ? "Save Changes" : "Add Service Team Member"}
          </Button>
        </div>
      </StyledForm>
    </StyledModal>
  );
};

export default HandymanModal;
